import * as ss from 'superstruct';
import {
  ConnectedAccountTypeInfo,
  connectedAccountUsesApiKey,
} from '../connected-accounts';
import { AdSources } from '../conversions/tracking/paid-source.resource';

/**
 * Ad platforms whose ad accounts the platform registers and pulls ad data
 * from. Values reuse `AdSources`, so paid sources, dbt and the Cloudflare KV
 * need no mapping (Microsoft Ads stays `bing`).
 */
export const AdAccountPlatforms = [
  'google',
  'meta',
  'bing',
  'openai',
] as const satisfies readonly AdSources[];

export const AdAccountPlatform = ss.enums(AdAccountPlatforms);

export type AdAccountPlatform = (typeof AdAccountPlatforms)[number];

export const isAdAccountPlatform = (
  value: string,
): value is AdAccountPlatform => ss.is(value, AdAccountPlatform);

/** Each ad platform's ad accounts. */
export const AdAccountTypes: Record<
  AdAccountPlatform,
  ConnectedAccountTypeInfo
> = {
  google: { label: 'Google Ads', identity: 'external-id' },
  meta: { label: 'Meta Ads', identity: 'external-id' },
  bing: { label: 'Microsoft Ads', identity: 'external-id' },
  // Its own API key reaches only that ad account; the rest go through our
  // platform-wide credentials.
  openai: { label: 'OpenAI Ads', identity: 'api-key' },
};

export const AdAccountPlatformLabels: Record<AdAccountPlatform, string> = {
  google: AdAccountTypes.google.label,
  meta: AdAccountTypes.meta.label,
  bing: AdAccountTypes.bing.label,
  openai: AdAccountTypes.openai.label,
};

export const adAccountUsesApiKey = (platform: AdAccountPlatform): boolean =>
  connectedAccountUsesApiKey(AdAccountTypes, platform);

/**
 * Google Local Services campaigns are reported under this campaign id, for both
 * LSA lead conversions and LSA spend. There's one LSA campaign per customer.
 */
export const GoogleLsaCampaignId = 'LSA';
